import { supabase } from "@/integrations/supabase/client";

const KNOWN_HOSTS = [
  "localhost",
  "fastblog.com.br",
  "www.fastblog.com.br",
  "fastblog.lovable.app",
];

/**
 * Returns true when the current hostname is a custom domain
 * (i.e., not fastblog's own domains or localhost).
 */
export function isCustomDomain(): boolean {
  const host = window.location.hostname;
  if (KNOWN_HOSTS.includes(host)) return false;
  if (host.endsWith(".lovable.app")) return false;
  if (host.endsWith(".lovableproject.com")) return false;
  return true;
}

/**
 * Resolves a project by its custom domain (blog_custom_domain column).
 * Returns the project row or null if not found.
 */
export async function resolveProjectByDomain(domain: string) {
  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("blog_custom_domain", domain)
    .eq("blog_platform", "fastblog")
    .maybeSingle();

  if (error || !data) return null;
  return data;
}

/**
 * Slugifies a blog name for URL matching (same logic as PublicBlog pages).
 */
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function articlePathSegment(article: { slug?: string | null; title?: string | null; id: string }): string {
  const fromSlug = article.slug ? slugify(article.slug) : "";
  if (fromSlug) return fromSlug;
  const fromTitle = article.title ? slugify(article.title) : "";
  return fromTitle || article.id;
}

/**
 * Resolves a hosted (Fastblog-platform) project by its public slug.
 * Tries cache → narrow column queries (blog_name, name) → fallback to a
 * full list scan (legacy behavior). The result is cached for 24h to make
 * subsequent page loads instant.
 */
export async function resolveProjectBySlug<T = any>(
  slug: string,
  selectColumns: string,
): Promise<T | null> {
  if (!slug) return null;

  const lowerSlug = slug.toLowerCase();

  // Try targeted lookups first — far cheaper than scanning all hosted projects.
  const tryMatch = (rows: any[] | null | undefined): T | null => {
    if (!rows || rows.length === 0) return null;
    const exact = rows.find(
      (r) => slugify(r.blog_name || r.name) === lowerSlug,
    );
    return (exact as T) || null;
  };

  // Targeted ILIKE (handles spaces vs hyphens roughly). Falls back to .name
  // when blog_name is null.
  const slugSpaced = lowerSlug.replace(/-/g, "%");
  const [byBlogName, byName] = await Promise.all([
    supabase
      .from("projects")
      .select(selectColumns)
      .eq("blog_platform", "fastblog")
      .ilike("blog_name", slugSpaced)
      .limit(20),
    supabase
      .from("projects")
      .select(selectColumns)
      .eq("blog_platform", "fastblog")
      .is("blog_name", null)
      .ilike("name", slugSpaced)
      .limit(20),
  ]);

  // We no longer do a full table scan: the edge function `public-blog-resolve`
  // covers anything the narrow ILIKE misses via the indexed RPC, and the
  // original scan was the main cause of slow TTFB on first paint.
  return tryMatch(byBlogName.data as any[]) ?? tryMatch(byName.data as any[]);
}
